import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Search, X } from "lucide-react";
import { STORE_CATEGORIES, ProductCard, StoreToTrainingBand } from "../../store/components";

const API = process.env.REACT_APP_BACKEND_URL;

const StoreCatalog = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const recommended = params.get("recommended") === "1";
  const [search, setSearch] = useState(params.get("search") || "");
  const [sort, setSort] = useState("newest");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    if (category) q.set("category", category);
    if (recommended) q.set("recommended", "true");
    if (search) q.set("search", search);
    q.set("sort", sort);
    const t = setTimeout(() => {
      axios.get(`${API}/api/store/products?${q}`).then((r) => setProducts(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [category, recommended, search, sort]);

  const setFilter = (patch) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  return (
    <>
      <section className="bg-dwc-soft pb-10 pt-36 md:pt-40" data-testid="catalog-hero">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-dwc-green">DWC Store</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-dwc-charcoal sm:text-4xl">
            {recommended ? "DWC Recommended" : category ? STORE_CATEGORIES.find((c) => c.slug === category)?.name || "Shop" : "All Products"}
          </h1>
          <div className="mt-6 flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-dwc-charcoal/40" aria-hidden="true" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder='Search "night glasses", "phone holder", "microfiber"...'
                data-testid="store-search-input"
                aria-label="Search products"
                className="w-full rounded-xl border border-gray-200 bg-white py-3.5 pl-11 pr-10 text-sm font-semibold text-dwc-charcoal outline-none transition-colors focus:border-dwc-green"
              />
              {search && (
                <button onClick={() => setSearch("")} aria-label="Clear search" data-testid="store-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-dwc-charcoal/40 hover:text-dwc-crimson">
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              data-testid="store-sort-select"
              aria-label="Sort products"
              className="rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-bold text-dwc-charcoal outline-none focus:border-dwc-green"
            >
              <option value="newest">Newest</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
            </select>
          </div>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="category-filters">
            <button
              onClick={() => setFilter({ category: "", recommended: "" })}
              data-testid="filter-all"
              className={`rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-wide transition-colors ${!category && !recommended ? "bg-dwc-green text-white" : "border border-dwc-green/25 bg-white text-dwc-green hover:bg-dwc-soft"}`}
            >
              All
            </button>
            <button
              onClick={() => setFilter({ recommended: recommended ? "" : "1", category: "" })}
              data-testid="filter-recommended"
              className={`rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-wide transition-colors ${recommended ? "bg-dwc-crimson text-white" : "border border-dwc-crimson/30 bg-white text-dwc-crimson hover:bg-dwc-crimson/5"}`}
            >
              DWC Recommended
            </button>
            {STORE_CATEGORIES.map((c) => (
              <button
                key={c.slug}
                onClick={() => setFilter({ category: category === c.slug ? "" : c.slug, recommended: "" })}
                data-testid={`filter-${c.slug}`}
                className={`rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-wide transition-colors ${category === c.slug ? "bg-dwc-green text-white" : "border border-dwc-green/25 bg-white text-dwc-green hover:bg-dwc-soft"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-white py-14 md:py-20" data-testid="catalog-grid-section">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {loading ? (
            <p className="py-20 text-center text-sm font-bold uppercase tracking-widest text-dwc-charcoal/40">Loading products...</p>
          ) : products.length === 0 ? (
            <p className="py-20 text-center text-sm font-semibold text-dwc-charcoal/60" data-testid="no-products-message">
              No products found. Try a different search or category.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-4 md:gap-5 lg:grid-cols-4">
              {products.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>
      <StoreToTrainingBand />
    </>
  );
};

export default StoreCatalog;
